import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { trainingService } from '../../services';
import { Modal, Select, Button, PageHeader } from '../../components';
import { TrainingSkeleton } from '../../skeletons';
import { toast } from 'react-hot-toast';
import { Plus, GraduationCap, BookOpen, CheckCircle2, Clock, Trash2, UserPlus, Layers, FileText, Upload, Eye, Bell, AlertCircle, Download, Filter } from 'lucide-react';

/**
 * Training — managers define courses, assign them to carers, and track progress.
 * Carers move an assignment pending → in_progress → done from the mobile app;
 * managers can also override the status here. Backed by
 * /api/carehome-manager/training.
 */

const CATEGORIES = [
  'Core mandatory/general',
  'Care-specific',
  'Domiciliary care specific',
  'Care home specific',
  'Office/management',
  'Custom',
];

// Each category gets a stable colour used for its badge + chip dot.
const CATEGORY_COLORS = {
  'Core mandatory/general': '#6366F1',
  'Care-specific': '#0EA5E9',
  'Domiciliary care specific': '#14B8A6',
  'Care home specific': '#F59E0B',
  'Office/management': '#8B5CF6',
  'Custom': '#64748B',
};
const catColor = (c) => CATEGORY_COLORS[c] || '#64748B';

// Category filter value meaning "every category".
const ALL_CATEGORIES = '';

/**
 * Category dropdown options with how many courses each holds. Every standard
 * category is listed (an empty one still reads "(0)", so it's clear there is
 * nothing there yet), plus any older category a course still carries.
 */
const categoryOptions = (trainings) => {
  const counts = {};
  trainings.forEach((t) => { if (t.category) counts[t.category] = (counts[t.category] || 0) + 1; });
  const extra = Object.keys(counts).filter((c) => !CATEGORIES.includes(c)).sort();
  return [
    { value: ALL_CATEGORIES, label: `All categories (${trainings.length})` },
    ...[...CATEGORIES, ...extra].map((c) => ({ value: c, label: `${c} (${counts[c] || 0})` })),
  ];
};
const inCategory = (t, category) => category === ALL_CATEGORIES || t.category === category;

// Status vocabulary shown on the carer board — mapped onto the backend's four
// states. The manager review workflow reads:
//   pending → carer uploads → completed (awaiting review) → verified (approved)
//   manager can bounce back to in_progress (changes requested) for a re-upload.
const STATUS_UI = {
  pending: { label: 'Pending', badge: 'bg-amber-100 dark:bg-amber-900/10 text-amber-700 dark:text-amber-400', icon: null },
  in_progress: { label: 'Changes requested', badge: 'bg-rose-50 dark:bg-red-900/10 text-rose-600 dark:text-red-400 border border-rose-200 dark:border-red-900/50', icon: AlertCircle },
  completed: { label: 'Awaiting review', badge: 'bg-orange-50 dark:bg-orange-900/10 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-900/50', icon: Bell },
  verified: { label: 'Approved', badge: 'bg-green-50 dark:bg-emerald-900/10 text-green-700 dark:text-emerald-400 border border-green-200 dark:border-emerald-900/50', icon: CheckCircle2 },
};

const API_ORIGIN = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

const initials = (name) => (name || '?').trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';

const Tile = ({ label, value, icon: Icon, accent, iconBg }) => (
  <div
    className="group relative bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)] p-5 transition-all hover:shadow-[0_2px_4px_rgba(15,23,42,0.06),0_10px_24px_-6px_rgba(15,23,42,0.12)]"
    style={{ borderBottomColor: accent, borderBottomWidth: '3px' }}
  >
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <div className="text-[11px] font-bold tracking-wider text-[#94A3B8] dark:text-gray-500 uppercase">{label}</div>
        <div className="text-[34px] leading-none font-extrabold text-[#0B1220] dark:text-gray-100 mt-3 tabular-nums">{value}</div>
      </div>
      <span className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${iconBg}`}><Icon className="w-[22px] h-[22px]" /></span>
    </div>
  </div>
);

const CategoryBadge = ({ category }) => {
  if (!category) return null;
  const c = catColor(category);
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-md" style={{ color: c, background: `${c}18` }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c }} />
      {category}
    </span>
  );
};

const emptyTraining = { name: '', category: 'Core mandatory/general', description: '' };

// How many rows each box shows before "See more". Courses is a 3-col grid on
// desktop, so 6 cards ≈ 2 rows; the carer board is one row per carer.
const COURSES_PREVIEW = 6;
const CARERS_PREVIEW = 2;

const Training = () => {
  const [board, setBoard] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyTraining);
  const [savingCreate, setSavingCreate] = useState(false);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({ category: ALL_CATEGORIES, training_id: '', due_date: '', user_ids: [] });
  const [savingAssign, setSavingAssign] = useState(false);

  // Which assignment's "Review" popover is open (holds the assignment id).
  const [reviewOpen, setReviewOpen] = useState(null);

  // Evidence viewer — { url, name } when open. Shows the file in an in-app
  // modal (image inline) with a download option, instead of opening a new tab.
  const [evidence, setEvidence] = useState(null);

  // "See more" toggles — each box shows a couple of rows until expanded.
  const [showAllCourses, setShowAllCourses] = useState(false);
  const [showAllCarers, setShowAllCarers] = useState(false);

  // Courses box category filter.
  const [courseCategory, setCourseCategory] = useState(ALL_CATEGORIES);

  const fetchBoard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await trainingService.getBoard();
      if (res.success) {
        setBoard(res.data || []);
        setTrainings(res.trainings || []);
      } else {
        setError(res.message || 'Failed to load training');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading training');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  const summary = useMemo(() => {
    const all = board.flatMap((m) => m.assignments || []);
    return {
      courses: trainings.length,
      assigned: all.length,
      inProgress: all.filter((a) => a.status === 'in_progress').length,
      // Assignments the carer has submitted evidence for and are waiting on a
      // manager to approve / request changes.
      awaitingReview: all.filter((a) => a.status === 'completed').length,
      done: all.filter((a) => a.status === 'completed' || a.status === 'verified').length,
    };
  }, [board, trainings]);

  // How many staff each course is assigned to.
  const assignedCount = useMemo(() => {
    const map = {};
    board.forEach((m) => (m.assignments || []).forEach((a) => { map[a.training_id] = (map[a.training_id] || 0) + 1; }));
    return map;
  }, [board]);

  const categoryOpts = useMemo(() => categoryOptions(trainings), [trainings]);
  const visibleCourses = useMemo(
    () => trainings.filter((t) => inCategory(t, courseCategory)),
    [trainings, courseCategory]
  );

  // A new course starts in the category the Courses box is showing, if any.
  const openCreate = () => {
    setForm({ ...emptyTraining, category: CATEGORIES.includes(courseCategory) ? courseCategory : emptyTraining.category });
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Training name is required'); return; }
    try {
      setSavingCreate(true);
      const res = await trainingService.createTraining({ name: form.name.trim(), category: form.category, description: form.description });
      if (res.success) {
        toast.success('Training created');
        // Don't let the Courses filter hide the course just made.
        if (!inCategory({ category: form.category }, courseCategory)) setCourseCategory(form.category);
        setCreateOpen(false); setForm(emptyTraining); fetchBoard();
      }
      else toast.error(res.message || 'Failed to create');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create');
    } finally { setSavingCreate(false); }
  };

  // From a course card the modal opens on that course's category; from the
  // header button, on whatever category the Courses box is filtered to.
  const openAssign = (trainingId) => {
    const course = trainings.find((t) => t.id === trainingId);
    const category = course ? (course.category || ALL_CATEGORIES) : courseCategory;
    const first = course || trainings.find((t) => inCategory(t, category));
    setAssignForm({ category, training_id: first ? String(first.id) : '', due_date: '', user_ids: [] });
    setAssignOpen(true);
  };

  // Changing the category keeps the chosen course only if it is in the new one.
  const pickAssignCategory = (category) => setAssignForm((f) => {
    const current = trainings.find((t) => String(t.id) === f.training_id);
    if (current && inCategory(current, category)) return { ...f, category };
    const first = trainings.find((t) => inCategory(t, category));
    return { ...f, category, training_id: first ? String(first.id) : '' };
  });

  const toggleStaff = (id) => setAssignForm((f) => ({
    ...f,
    user_ids: f.user_ids.includes(id) ? f.user_ids.filter((x) => x !== id) : [...f.user_ids, id],
  }));

  const handleAssign = async () => {
    if (!assignForm.training_id) { toast.error('Choose a training'); return; }
    if (assignForm.user_ids.length === 0) { toast.error('Select at least one carer'); return; }
    try {
      setSavingAssign(true);
      const res = await trainingService.assign({ training_id: Number(assignForm.training_id), user_ids: assignForm.user_ids, due_date: assignForm.due_date || null });
      if (res.success) { toast.success(res.message || 'Assigned'); setAssignOpen(false); fetchBoard(); }
      else toast.error(res.message || 'Failed to assign');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign');
    } finally { setSavingAssign(false); }
  };

  const changeStatus = async (assignmentId, status) => {
    try {
      const res = await trainingService.updateStatus(assignmentId, status);
      if (res.success) {
        if (status === 'verified') toast.success('Marked verified');
        fetchBoard(); // refresh verified_by / completed_at from the server
      } else toast.error(res.message || 'Failed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    }
  };

  // Re-upload evidence after changes were requested — attaches the new file and
  // moves the assignment back to "awaiting review" (completed) for the manager.
  const reupload = async (assignment, file) => {
    if (!file) return;
    try {
      const res = await trainingService.updateStatus(assignment.id, 'completed', file);
      if (res.success) { toast.success('Evidence uploaded — awaiting review'); fetchBoard(); }
      else toast.error(res.message || 'Upload failed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    }
  };

  const removeAssignment = async (assignmentId) => {
    try {
      const res = await trainingService.unassign(assignmentId);
      if (res.success) {
        toast.success('Removed');
        setBoard((prev) => prev.map((m) => ({ ...m, assignments: m.assignments.filter((a) => a.id !== assignmentId) })));
      } else toast.error(res.message || 'Failed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove');
    }
  };

  if (loading && board.length === 0) return <TrainingSkeleton />;

  return (
    <div className="training-page w-full space-y-5 pb-12 font-sans">
      {/* Header */}
      <PageHeader
        title="Training"
        subtitle="Create courses, assign them to carers and track completion."
        actions={(
          <>
            <Button variant="secondary" leftIcon={<Plus className="w-4 h-4" />} onClick={() => openCreate()}>
              Create training
            </Button>
            <Button variant="primary" leftIcon={<UserPlus className="w-4 h-4" />} disabled={trainings.length === 0} onClick={() => openAssign()}>
              Assign training
            </Button>
          </>
        )}
      />

      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Tile label="Courses" value={summary.courses} icon={BookOpen} accent="#6366F1" iconBg="bg-indigo-50 text-indigo-600" />
        <Tile label="Assigned" value={summary.assigned} icon={GraduationCap} accent="#2563EB" iconBg="bg-blue-50 text-blue-600" />
        <Tile label="In progress" value={summary.inProgress} icon={Clock} accent="#F59E0B" iconBg="bg-amber-50 text-amber-600" />
        <Tile label="To review" value={summary.awaitingReview} icon={Bell} accent="#F97316" iconBg="bg-orange-50 text-orange-600" />
        <Tile label="Completed" value={summary.done} icon={CheckCircle2} accent="#10B981" iconBg="bg-green-50 text-green-600" />
      </div>

      {/* Courses catalogue */}
      <div className="bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)] p-5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-3 mb-4">
          <Layers className="w-4 h-4 text-[#6366F1]" />
          <h2 className="text-[15px] font-semibold text-[#1E293B] dark:text-gray-200">Courses</h2>
          <span className="text-[12px] text-[#94A3B8] dark:text-gray-500">
            ({courseCategory === ALL_CATEGORIES ? trainings.length : `${visibleCourses.length} of ${trainings.length}`})
          </span>
          {trainings.length > 0 && (
            <Select
              value={courseCategory}
              onChange={setCourseCategory}
              options={categoryOpts}
              leftIcon={<Filter className="w-4 h-4" />}
              size="sm"
              className="w-full sm:w-[270px] sm:ml-auto"
            />
          )}
        </div>
        {trainings.length === 0 ? (
          <div className="text-[13px] text-[#64748B] dark:text-gray-400 bg-[#F8FAFC] dark:bg-[#20242c] border border-dashed border-[#E2E8F0] dark:border-gray-700 rounded-xl px-4 py-6 text-center">
            No courses yet. Click <span className="font-semibold">Create training</span> to add your first one.
          </div>
        ) : visibleCourses.length === 0 ? (
          <div className="text-[13px] text-[#64748B] dark:text-gray-400 bg-[#F8FAFC] dark:bg-[#20242c] border border-dashed border-[#E2E8F0] dark:border-gray-700 rounded-xl px-4 py-6 text-center">
            No courses in <span className="font-semibold">{courseCategory}</span> yet.{' '}
            <button onClick={() => openCreate()} className="font-semibold text-[#2563EB] hover:underline">Create one</button>
            {' '}or{' '}
            <button onClick={() => setCourseCategory(ALL_CATEGORIES)} className="font-semibold text-[#2563EB] hover:underline">show all categories</button>.
          </div>
        ) : (
          <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {(showAllCourses ? visibleCourses : visibleCourses.slice(0, COURSES_PREVIEW)).map((t) => (
              <div key={t.id} className="border border-[#EEF2F8] dark:border-gray-800 bg-[#FBFCFE] dark:bg-[#20242c] rounded-2xl p-4 hover:bg-white dark:hover:bg-[#262b33] hover:border-[#E3E9F3] hover:shadow-[0_4px_10px_-3px_rgba(15,23,42,0.08)] transition group">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[14px] font-semibold text-[#1E293B] dark:text-gray-200 truncate">{t.name}</div>
                    <div className="mt-1.5"><CategoryBadge category={t.category} /></div>
                  </div>
                  <span className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ background: catColor(t.category) }}>
                    <GraduationCap className="w-4 h-4" />
                  </span>
                </div>
                {t.description && <p className="text-[12px] text-[#64748B] dark:text-gray-400 mt-2 line-clamp-2">{t.description}</p>}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F1F5F9] dark:border-gray-800">
                  <span className="text-[12px] text-[#64748B] dark:text-gray-400"><b className="text-[#1E293B] dark:text-gray-200">{assignedCount[t.id] || 0}</b> assigned</span>
                  <button onClick={() => openAssign(t.id)} className="text-[12px] font-semibold text-[#2563EB] hover:underline">Assign →</button>
                </div>
              </div>
            ))}
          </div>
          {visibleCourses.length > COURSES_PREVIEW && (
            <div className="mt-4 text-center">
              <button
                onClick={() => setShowAllCourses((v) => !v)}
                className="text-[13px] font-semibold text-blue-600 hover:text-blue-700 hover:underline"
              >
                {showAllCourses ? 'See less' : `See more (${visibleCourses.length - COURSES_PREVIEW})`}
              </button>
            </div>
          )}
          </>
        )}
      </div>

      {/* Carer board */}
      <div className="bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)]">
        <div className="px-6 py-3 border-b border-[#EEF2F8] dark:border-gray-800 bg-[#F8FAFC] dark:bg-[#20242c] rounded-t-[20px] flex items-center justify-between gap-3">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#64748B] dark:text-gray-400">Carers &amp; progress</span>
          {summary.awaitingReview > 0 && (
            <span
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-900/50 rounded-full px-3 py-1"
              title={`${summary.awaitingReview} training${summary.awaitingReview === 1 ? '' : 's'} awaiting your review`}
            >
              <Bell className="w-3.5 h-3.5" />
              {summary.awaitingReview} in review
            </span>
          )}
        </div>
        {error ? (
          <div className="p-12 text-center">
            <div className="bg-rose-50 dark:bg-red-900/10 border border-rose-100 dark:border-red-900/50 text-rose-600 dark:text-red-400 px-6 py-4 rounded-xl inline-block">{error}</div>
            <button onClick={fetchBoard} className="block mx-auto mt-4 text-sm text-blue-600 font-medium hover:underline">Try Again</button>
          </div>
        ) : board.length === 0 ? (
          <div className="py-20 text-center text-[14px] text-[#64748B] dark:text-gray-400">No carers found.</div>
        ) : (
          <>
          <div className="divide-y divide-[#EEF2F7] dark:divide-gray-800">
            {(showAllCarers ? board : board.slice(0, CARERS_PREVIEW)).map((m) => {
              const total = m.assignments.length;
              // "Done" = approved (verified). Drives the progress bar + n/total.
              const done = m.assignments.filter((a) => a.status === 'verified').length;
              const pct = total ? Math.round((done / total) * 100) : 0;
              return (
                <div key={m.id} className="flex flex-col lg:flex-row lg:items-start gap-4 px-6 py-5">
                  {/* carer */}
                  <div className="w-full lg:w-[230px] shrink-0 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[13px] font-black shrink-0">{initials(m.name)}</div>
                    <div className="min-w-0">
                      <div className="text-[14px] font-semibold text-[#1E293B] dark:text-gray-200 truncate">{m.name}</div>
                      <div className="text-[12px] text-[#94A3B8] dark:text-gray-500">{m.role_label} · {m.code}</div>
                      {total > 0 && (
                        <div className="mt-1.5 flex items-center gap-2">
                          <div className="w-24 h-1.5 rounded-full bg-[#EEF2F7] dark:bg-gray-800 overflow-hidden">
                            <div className="h-full rounded-full bg-green-500" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[11px] font-semibold text-[#64748B] dark:text-gray-400">{done}/{total}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* assignments — one card per assigned course */}
                  <div className="flex-1 flex flex-wrap gap-3">
                    {total === 0 ? (
                      <button onClick={() => openAssign()} className="text-[13px] text-[#2563EB] font-medium hover:underline">+ Assign a course</button>
                    ) : m.assignments.map((a) => {
                      const ui = STATUS_UI[a.status] || STATUS_UI.pending;
                      const StatusIcon = ui.icon;
                      const proofHref = a.proof_url ? `${API_ORIGIN}${a.proof_url}` : null;
                      const fileName = a.proof_url ? a.proof_url.split('/').pop() : null;
                      return (
                        <div key={a.id} className="relative w-full sm:w-[300px] border border-[#EEF2F8] dark:border-gray-800 rounded-2xl p-3.5 bg-[#FBFCFE] dark:bg-[#20242c] hover:bg-white dark:hover:bg-[#262b33] hover:border-[#E3E9F3] hover:shadow-[0_4px_10px_-3px_rgba(15,23,42,0.08)] transition flex flex-col gap-3">
                          {/* header: category dot + name + status badge + remove */}
                          <div className="flex items-start gap-2">
                            <span className="w-2 h-2 rounded-full mt-[5px] shrink-0" style={{ background: catColor(a.category) }} title={a.category || ''} />
                            <span className="text-[13px] font-semibold text-[#1E293B] dark:text-gray-200 flex-1 min-w-0 truncate">{a.training_name}</span>
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-md inline-flex items-center gap-1 shrink-0 ${ui.badge}`}>
                              {StatusIcon && <StatusIcon className="w-3 h-3" />}{ui.label}
                            </span>
                            <button onClick={() => removeAssignment(a.id)} className="text-[#B6BECB] hover:text-rose-500 p-0.5 shrink-0" title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>

                          {/* evidence file (if uploaded) */}
                          {proofHref ? (
                            <button type="button" onClick={() => setEvidence({ url: proofHref, name: fileName })} className="flex items-center gap-2 text-[12px] text-[#475569] dark:text-gray-300 bg-[#F8FAFC] dark:bg-[#31373f] border border-[#E2E8F0] dark:border-gray-700 rounded-lg px-2.5 py-1.5 hover:bg-[#F1F5F9] dark:hover:bg-[#383e47] min-w-0 text-left" title={fileName}>
                              <FileText className="w-3.5 h-3.5 text-[#94A3B8] shrink-0" />
                              <span className="truncate">{fileName}</span>
                            </button>
                          ) : a.status !== 'pending' ? (
                            <div className="text-[12px] text-[#94A3B8] dark:text-gray-500 italic px-0.5">No evidence uploaded</div>
                          ) : null}

                          {/* status-specific action */}
                          <div className="relative">
                            {a.status === 'pending' && (
                              <Select
                                value={a.status}
                                onChange={(v) => changeStatus(a.id, v)}
                                options={[
                                  { value: 'pending', label: 'Pending' },
                                  { value: 'in_progress', label: 'Changes requested' },
                                  { value: 'completed', label: 'Awaiting review' },
                                  { value: 'verified', label: 'Approved' },
                                ]}
                                size="sm"
                                className="w-full"
                              />
                            )}

                            {a.status === 'in_progress' && (
                              <label className="w-full inline-flex items-center justify-center gap-1.5 text-[12px] font-semibold text-rose-600 border border-rose-200 bg-white rounded-lg px-3 py-1.5 hover:bg-rose-50 cursor-pointer">
                                <Upload className="w-3.5 h-3.5" /> Re-upload
                                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { reupload(a, e.target.files?.[0]); e.target.value = ''; }} />
                              </label>
                            )}

                            {a.status === 'completed' && (
                              <>
                                <button onClick={() => setReviewOpen(reviewOpen === a.id ? null : a.id)} className="w-full inline-flex items-center justify-center gap-1.5 text-[12px] font-semibold text-orange-600 border border-orange-300 bg-white rounded-lg px-3 py-1.5 hover:bg-orange-50">
                                  Review <Bell className="w-3.5 h-3.5" />
                                </button>
                                {reviewOpen === a.id && (
                                  <>
                                    <div className="fixed inset-0 z-10" onClick={() => setReviewOpen(null)} />
                                    <div className="absolute z-20 right-0 mt-1 w-48 bg-white dark:bg-[#262b33] border border-[#E2E8F0] dark:border-gray-800 rounded-lg shadow-lg py-1">
                                      {proofHref && (
                                        <button type="button" onClick={() => { setEvidence({ url: proofHref, name: fileName }); setReviewOpen(null); }} className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-[#334155] dark:text-gray-200 hover:bg-[#F8FAFC] dark:hover:bg-[#31373f]">
                                          <Eye className="w-3.5 h-3.5" /> View evidence
                                        </button>
                                      )}
                                      <button onClick={() => { changeStatus(a.id, 'verified'); setReviewOpen(null); }} className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-green-700 hover:bg-green-50">
                                        <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                                      </button>
                                      <button onClick={() => { changeStatus(a.id, 'in_progress'); setReviewOpen(null); }} className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-rose-600 hover:bg-rose-50">
                                        <AlertCircle className="w-3.5 h-3.5" /> Request changes
                                      </button>
                                    </div>
                                  </>
                                )}
                              </>
                            )}

                            {a.status === 'verified' && (
                              proofHref ? (
                                <button type="button" onClick={() => setEvidence({ url: proofHref, name: fileName })} className="w-full inline-flex items-center justify-center gap-1.5 text-[12px] font-semibold text-green-700 dark:text-emerald-400 border border-green-300 dark:border-emerald-900/50 bg-white dark:bg-[#262b33] rounded-lg px-3 py-1.5 hover:bg-green-50 dark:hover:bg-emerald-900/20">
                                  <Eye className="w-3.5 h-3.5" /> View evidence
                                </button>
                              ) : (
                                <div className="w-full text-center text-[12px] text-[#94A3B8] dark:text-gray-500 italic px-3 py-1.5 border border-dashed border-[#E2E8F0] dark:border-gray-700 rounded-lg">Approved · no file</div>
                              )
                            )}

                            {a.status === 'verified' && a.verified_by && (
                              <p className="text-[10px] text-emerald-700 mt-1.5 text-center" title={`Verified ${a.verified_at ? new Date(a.verified_at).toLocaleDateString('en-GB') : ''}`}>Approved by {a.verified_by}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          {board.length > CARERS_PREVIEW && (
            <div className="px-6 py-3 border-t border-[#EEF2F8] dark:border-gray-800 text-center">
              <button
                onClick={() => setShowAllCarers((v) => !v)}
                className="text-[13px] font-semibold text-blue-600 hover:text-blue-700 hover:underline"
              >
                {showAllCarers ? 'See less' : `See more (${board.length - CARERS_PREVIEW})`}
              </button>
            </div>
          )}
          </>
        )}
      </div>

      {/* Create training modal */}
      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} title="Create training" maxWidth="max-w-[520px]">
        <div className="space-y-4">
          <div>
            <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">Training name</label>
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Moving &amp; Handling" className="tinp" />
          </div>
          <div>
            <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">Category</label>
            <Select
              value={form.category}
              onChange={(v) => setForm((f) => ({ ...f, category: v }))}
              options={CATEGORIES.map((c) => ({ value: c, label: c }))}
              className="w-full"
            />
            <div className="mt-2"><CategoryBadge category={form.category} /></div>
          </div>
          <div>
            <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">Description (optional)</label>
            <textarea rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="tinp resize-none" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#EEF2F8] dark:border-gray-800">
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleCreate} loading={savingCreate} disabled={savingCreate}>Create</Button>
          </div>
        </div>
      </Modal>

      {/* Assign training modal */}
      <Modal isOpen={assignOpen} onClose={() => setAssignOpen(false)} title="Assign training" maxWidth="max-w-[560px]">
        {trainings.length === 0 ? (
          <p className="text-[13px] text-[#64748B] dark:text-gray-400 py-4">Create a training first, then assign it to carers.</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">Category</label>
                <Select
                  value={assignForm.category}
                  onChange={pickAssignCategory}
                  options={categoryOpts}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">Training</label>
                <Select
                  value={assignForm.training_id}
                  onChange={(v) => setAssignForm((f) => ({ ...f, training_id: v }))}
                  options={trainings
                    .filter((t) => inCategory(t, assignForm.category))
                    .map((t) => ({
                      value: String(t.id),
                      // The category is only worth repeating when every category is listed.
                      label: assignForm.category === ALL_CATEGORIES && t.category ? `${t.name} — ${t.category}` : t.name,
                    }))}
                  placeholder="No courses in this category"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">Due date (optional)</label>
                <input type="date" value={assignForm.due_date} onChange={(e) => setAssignForm((f) => ({ ...f, due_date: e.target.value }))} className="tinp" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[12px] font-bold text-[#1E293B] dark:text-gray-200">Carers ({assignForm.user_ids.length} selected)</label>
                <button
                  onClick={() => setAssignForm((f) => ({ ...f, user_ids: f.user_ids.length === board.length ? [] : board.map((m) => m.id) }))}
                  className="text-[11px] font-semibold text-[#2563EB] hover:underline"
                >
                  {assignForm.user_ids.length === board.length ? 'Clear all' : 'Select all'}
                </button>
              </div>
              <div className="max-h-[240px] overflow-y-auto border border-[#E2E8F0] dark:border-gray-800 rounded-lg divide-y divide-[#F1F5F9] dark:divide-gray-800">
                {board.map((m) => (
                  <label key={m.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#F8FAFC] dark:hover:bg-[#31373f]">
                    <input type="checkbox" checked={assignForm.user_ids.includes(m.id)} onChange={() => toggleStaff(m.id)} />
                    <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[11px] font-black">{initials(m.name)}</span>
                    <span className="text-[13px] text-[#1E293B] dark:text-gray-200">{m.name}</span>
                    <span className="text-[11px] text-[#94A3B8] dark:text-gray-500">{m.role_label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#EEF2F8] dark:border-gray-800">
              <Button variant="ghost" onClick={() => setAssignOpen(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleAssign} loading={savingAssign} disabled={savingAssign}>Assign</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Evidence viewer — image/PDF preview inline, with a Download option */}
      {evidence && (() => {
        const src = evidence.url || '';
        const isImg = /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(evidence.name || src);
        const isPdf = /\.pdf(\?|$)/i.test(evidence.name || src);
        return (
          <Modal
            isOpen={!!evidence}
            onClose={() => setEvidence(null)}
            title="Evidence"
            maxWidth="max-w-3xl"
            footer={
              <div className="flex items-center justify-between w-full gap-3">
                <span className="text-[12px] text-[#64748B] dark:text-gray-400 truncate">{evidence.name}</span>
                <a
                  href={src}
                  download={evidence.name || true}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 shrink-0"
                >
                  <Download className="w-4 h-4" /> Download
                </a>
              </div>
            }
          >
            <div className="flex items-center justify-center bg-[#0B1220]/5 dark:bg-black/30 rounded-xl min-h-[320px] max-h-[70vh] overflow-auto p-3">
              {isImg ? (
                <img src={src} alt={evidence.name} className="max-w-full max-h-[65vh] object-contain rounded-lg" />
              ) : isPdf ? (
                <iframe src={src} title={evidence.name || 'Evidence'} className="w-full h-[68vh] rounded-lg bg-white" />
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto text-[#94A3B8] mb-3" />
                  <p className="text-[13px] text-[#475569] dark:text-gray-300">Preview isn't available for this file type.</p>
                  <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-1">Use the Download button to open it.</p>
                </div>
              )}
            </div>
          </Modal>
        );
      })()}

      <style>{`
        .tinp{width:100%;padding:0.5rem 0.75rem;border:1px solid #E2E8F0;border-radius:0.6rem;font-size:13px;color:#0F172A;background:#fff;outline:none}
        .tinp:focus{border-color:#3B82F6;box-shadow:0 0 0 3px rgba(59,130,246,.1)}
        .training-page .line-clamp-2{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
        .training-page select{
          -webkit-appearance:none;-moz-appearance:none;appearance:none;
          background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
          background-repeat:no-repeat;background-position:right 0.5rem center;background-size:14px;padding-right:1.8rem;cursor:pointer;
        }
      `}</style>
    </div>
  );
};

export default Training;
